import React from "react";
import { Link } from "react-router-dom";
import {
  SparklesIcon,
  ChatBubbleLeftEllipsisIcon,
  MagnifyingGlassIcon,
  LightBulbIcon,
} from "@heroicons/react/24/outline";
import { FaLinkedinIn } from "react-icons/fa6";
import GlowButton from "./GlowButton";
import logo from "../assets/finai_nobg.png";

/**
 * Pre-filled query each "Ask <LLM>" button sends. Conversational + first-
 * person, frames a concrete user benefit, includes the URL so the LLM can
 * find the site. Mirrors the "Ask Gemini / Ask ChatGPT" pattern other
 * AI-first startups use as soft SEO for the LLM search era.
 */
const ASK_QUERY =
  "Tell me about FinAI (https://finaiglobal.com) and how I can use its AI Council to manage my whole portfolio — stocks, gold, crypto, real estate, FDs — without paying 1% AUM to a traditional financial advisor";

const ASK_BUTTONS = [
  {
    label: "Ask Gemini",
    Icon: SparklesIcon,
    accent: "text-sky-500",
    // Google Search's AI Mode tab is the only Gemini-powered surface that
    // accepts a prefilled query via URL. The bare gemini.google.com/app
    // route ignores ?q=. udm=50 = AI Mode tab.
    href: `https://www.google.com/search?udm=50&q=${encodeURIComponent(ASK_QUERY)}`,
  },
  {
    label: "Ask ChatGPT",
    Icon: ChatBubbleLeftEllipsisIcon,
    accent: "text-emerald-500",
    href: `https://chatgpt.com/?q=${encodeURIComponent(ASK_QUERY)}`,
  },
  {
    label: "Ask Claude",
    Icon: LightBulbIcon,
    accent: "text-orange-500",
    href: `https://claude.ai/new?q=${encodeURIComponent(ASK_QUERY)}`,
  },
  {
    label: "Ask Perplexity",
    Icon: MagnifyingGlassIcon,
    accent: "text-teal-500",
    href: `https://www.perplexity.ai/search?q=${encodeURIComponent(ASK_QUERY)}`,
  },
];

// To add X/Twitter later: re-import FaXTwitter and add
// { label: "FinAI on X", Icon: FaXTwitter, href: "https://x.com/<handle>" }
const SOCIAL_LINKS = [
  {
    label: "FinAI on LinkedIn",
    Icon: FaLinkedinIn,
    href: "https://www.linkedin.com/company/finai-global/",
  },
];

const COMPANY_LINKS = [
  { label: "Blog", to: "/blogs" },
  { label: "Community", to: "/community" },
  { label: "Pricing", to: "/pricing" },
  { label: "Contact", to: "/contact" },
];

const LEGAL_LINKS = [
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Terms of Service", to: "/terms" },
];

const AskButton = ({ label, Icon, accent, href }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/10 hover:border-gray-300 dark:hover:border-white/20 transition-colors"
  >
    <Icon className={`w-4 h-4 ${accent}`} />
    <span>{label}</span>
  </a>
);

const SocialButton = ({ label, Icon, href }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    title={label}
    className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:border-emerald-400/60 dark:hover:border-emerald-400/40 hover:-translate-y-0.5 transition-all"
  >
    <Icon className="w-4 h-4" />
  </a>
);

const LinkColumn = ({ heading, links }) => (
  <div>
    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-500 mb-4">
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-gradient-to-r from-emerald-400 to-blue-500" />
      {heading}
    </p>
    <ul className="space-y-2.5">
      {links.map((l) => (
        <li key={l.label}>
          <Link
            to={l.to}
            className="group inline-flex items-center text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <span className="inline-block transition-transform duration-200 group-hover:translate-x-0.5">
              {l.label}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const Footer = ({
  ctaHref = "/signup",
  ctaLabel = "Get started — it's free",
}) => (
  <footer className="relative overflow-hidden bg-gray-50/70 dark:bg-white/[0.015]">
    {/* Gradient top edge + soft glow */}
    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent" />
    <div
      aria-hidden
      className="absolute inset-x-0 top-0 h-24 bg-gradient-to-r from-emerald-500/20 via-blue-500/20 to-purple-500/20 blur-3xl opacity-60 dark:opacity-40 pointer-events-none"
    />
    {/* Faint brand mesh glow so the footer feels lit, not dead-black */}
    <div
      aria-hidden
      className="absolute -bottom-24 left-1/2 -translate-x-1/2 w-[60rem] h-[30rem] bg-mesh-dark opacity-[0.07] blur-2xl pointer-events-none"
    />

    <div className="relative container mx-auto px-6 max-w-7xl py-16 lg:py-20">
      {/* CTA BAND */}
      <div className="text-center max-w-3xl mx-auto pb-14 mb-14 border-b border-gray-200/70 dark:border-white/5">
        <h2 className="text-3xl md:text-5xl font-extrabold mb-5">
          Ready to put your whole net worth on FinAI?
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-10 leading-relaxed">
          Free to start. Link a brokerage in 60 seconds, add the rest yourself,
          and the Council goes to work.
        </p>
        <div className="flex justify-center">
          <GlowButton to={ctaHref} size="lg">
            {ctaLabel.toUpperCase()}
          </GlowButton>
        </div>
      </div>

      {/* LINK GRID */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-10 gap-y-12 mb-12">
        {/* Brand column — 2 cols on lg */}
        <div className="col-span-2 lg:col-span-2 pr-6">
          <div className="flex items-center gap-2 mb-4">
            <img src={logo} alt="FinAI" className="w-7 h-7" />
            <span className="font-extrabold text-2xl tracking-tight">FinAI</span>
          </div>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed max-w-xs">
            Track everything you own.
            <br />
            Ask anything.
          </p>

          <p className="text-xs text-gray-500 dark:text-gray-500 mt-8">
            © {new Date().getFullYear()} FinAI. All rights reserved.
          </p>
        </div>

        <LinkColumn heading="Company" links={COMPANY_LINKS} />
        <LinkColumn heading="Legal" links={LEGAL_LINKS} />
      </div>

      {/* ASK-AN-LLM BAND */}
      <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/50 dark:bg-white/[0.02] px-5 py-5 sm:px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-500">
            Learn more about FinAI
          </p>
          <span className="hidden sm:block w-px h-5 bg-gray-300/70 dark:bg-white/10" />
          {SOCIAL_LINKS.map((s) => (
            <SocialButton key={s.label} {...s} />
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {ASK_BUTTONS.map((b) => (
            <AskButton key={b.label} {...b} />
          ))}
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
